
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, Outlet, useParams, useNavigate } from 'react-router-dom';
import { WeddingProvider, useWedding } from './context/WeddingContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AiContextProvider, useAi } from './context/AiContext'; // Updated import
import { AiDrawer } from './components/AiDrawer'; // Import Drawer
import { getTranslation } from './i18n';
import { Hourglass, LayoutDashboard, Heart, Users, CheckSquare, CalendarDays, Wallet, Store, Armchair, FileText, Mail, Settings, Sun, Moon, Menu, LogOut, Sparkles, ChevronDown, ChevronRight, Hash, Gem, ScrollText, PartyPopper, UsersRound } from 'lucide-react';
import { slugify } from './constants';

// Pages
import Timeline from './pages/Timeline';
import Dashboard from './pages/Dashboard';
import Guests from './pages/Guests';
import Tasks from './pages/Tasks';
import Schedule from './pages/Schedule';
import Entertainment from './pages/Entertainment';
import WeddingTeam from './pages/WeddingTeam';
import Budget from './pages/Budget';
import Vendors from './pages/Vendors';
import RingsPage from './pages/Rings'; 
import CeremonyPage from './pages/Ceremony'; 
import SeatingPage from './pages/SeatingPage'; 
import Notes from './pages/Notes';
import Templates from './pages/Templates';
import SettingsPage from './pages/Settings';
import PublicPage from './pages/PublicPage';
import { INVITATION_PUBLIC_PATH } from './lib/invitation/urls';
import WeddingsPage from './pages/Weddings';
import LoginPage from './pages/Login';
import { ProtectedRoute } from './components/ProtectedRoute'; 

// --- SHARED COMPONENTS ---

interface SidebarItemProps {
  to: string;
  icon: any;
  label: string;
  active: boolean;
  onClick: () => void;
  isLocked?: boolean;
  children?: { label: string; to: string }[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  currentPath?: string;
  currentHash?: string;
}

const SidebarItem = ({ to, icon: Icon, label, active, onClick, isLocked, children, isExpanded, onToggleExpand, currentPath = '', currentHash = '' }: SidebarItemProps) => {
  if (isLocked) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50">
        <Icon size={18} strokeWidth={1.5} className="text-slate-400 dark:text-slate-500 shrink-0" />
        <span className="text-sm font-normal">{label}</span>
      </div>
    );
  }

  const linkBase = "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-150 text-sm font-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900";
  const linkDefault = "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-white";
  const linkActive = "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-medium";
  const iconDefault = "text-slate-400 dark:text-slate-400 shrink-0";
  const iconActive = "text-slate-700 dark:text-white shrink-0";

  return (
    <div className="mb-0.5">
      <div className="flex items-center justify-between group/item">
        <Link
          to={to}
          onClick={onClick}
          className={`flex-1 min-w-0 ${linkBase} ${active ? linkActive : linkDefault}`}
        >
          <Icon size={18} strokeWidth={1.5} className={active ? iconActive : iconDefault} />
          <span className="truncate">{label}</span>
        </Link>
        {children && children.length > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleExpand?.();
            }}
            className={`shrink-0 mr-1 p-1.5 rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
              active ? 'text-slate-700 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700' : 'text-slate-400 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Zavřít podmenu' : 'Otevřít podmenu'}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}
      </div>

      {/* Submenu */}
      {isExpanded && children && (
        <div className="pl-9 mt-0.5 space-y-0.5 animate-fade-in">
          {children.map((subItem) => {
            const hashIdx = subItem.to.indexOf('#');
            const subPath = hashIdx >= 0 ? subItem.to.slice(0, hashIdx) : subItem.to;
            const subHash = hashIdx >= 0 ? subItem.to.slice(hashIdx) : '';
            const isChildActive = currentPath === subPath && (subHash ? currentHash === subHash : !currentHash);
            return (
              <Link
                key={subItem.to}
                to={subItem.to}
                onClick={onClick}
                className={`block py-1.5 px-3 rounded-lg text-sm font-normal transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                  isChildActive
                    ? 'bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white font-medium'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {subItem.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Global Floating Action Button for AI (Failsafe)
const GlobalAiFab = () => {
  const { openCopilot } = useAi();
  return (
    <button 
      onClick={openCopilot}
      className="fixed bottom-6 right-6 z-[2000] w-12 h-12 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform hover:shadow-indigo-500/30 group"
      title="Otevřít AI Asistenta"
    >
      <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
      <span className="absolute -top-1 -right-1 flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sky-500"></span>
      </span>
    </button>
  );
};

const AiTriggerButton = () => {
  const { openCopilot } = useAi();
  return (
    <button 
      onClick={openCopilot}
      className="hidden md:flex bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 px-3 py-1.5 rounded-lg font-semibold text-xs items-center gap-1.5 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors border border-violet-100 dark:border-violet-800/50"
    >
      <Sparkles size={13} /> AI Asistent
    </button>
  );
};

// Layout for the Workspace Selection (Home) - No Sidebar
const HomeLayout = () => {
  const { theme, setTheme } = useWedding();
  const { signOut, profile } = useAuth();
  
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-[#0c0c0e] transition-colors duration-300">
      <header className="h-16 bg-white/80 dark:bg-[#121215]/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-6 lg:px-10 flex items-center justify-between sticky top-0 z-40">
         <Link to="/app" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                <Heart size={18} fill="white" />
            </div>
            <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">MarryPlan</span>
         </Link>
         <div className="flex items-center gap-4">
           <AiTriggerButton />
           
           {/* User info */}
           {profile && (
             <span className="hidden sm:block text-sm text-slate-500 dark:text-slate-400">
               {profile.username || profile.name}
             </span>
           )}
           
           <button 
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
           >
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
           </button>
           
           {/* Logout button */}
           <button 
              onClick={signOut}
              className="p-2.5 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
              title="Odhlásit se"
           >
              <LogOut size={16} />
           </button>
         </div>
      </header>
      <main className="flex-1 p-6 lg:p-10 max-w-7xl mx-auto w-full">
         <Outlet />
      </main>
    </div>
  );
};

// Layout for Wedding Details - With Sidebar
function useIsLg() {
  const [isLg, setIsLg] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 1024px)');
    const on = () => setIsLg(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return isLg;
}

const WeddingLayout = () => {
  const { weddingId } = useParams();
  const { switchWedding, wedding, locale, theme, setTheme, hasFeature } = useWedding();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isLg = useIsLg();
  const sidebarRef = React.useRef<HTMLElement>(null);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);

  // Persist expanded menu state
  const [expandedMenu, setExpandedMenu] = useState<string | null>(() => {
    return localStorage.getItem('sidebar_expanded_menu');
  });

  useEffect(() => {
    if (expandedMenu) {
      localStorage.setItem('sidebar_expanded_menu', expandedMenu);
    } else {
      localStorage.removeItem('sidebar_expanded_menu');
    }
  }, [expandedMenu]);

  // ESC closes drawer; focus trap when open on mobile
  useEffect(() => {
    if (!isSidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSidebarOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!isSidebarOpen || !sidebarRef.current) return;
    const el = sidebarRef.current;
    const focusables = el.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    const first = focusables[0];
    if (first) first.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const list = Array.from(focusables);
      const idx = list.indexOf(document.activeElement as HTMLElement);
      if (idx === -1) return;
      if (e.shiftKey) {
        if (idx === 0) {
          e.preventDefault();
          list[list.length - 1].focus();
        }
      } else {
        if (idx === list.length - 1) {
          e.preventDefault();
          list[0].focus();
        }
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [isSidebarOpen]);

  const location = useLocation();
  const t = (key: string) => getTranslation(locale, key);

  // Sync URL param with Context
  useEffect(() => {
    if (weddingId && (!wedding || wedding.id !== weddingId)) {
      switchWedding(weddingId);
    }
  }, [weddingId]);

  // Auto-expand menu if current path matches
  useEffect(() => {
    if (location.pathname.includes('/vendors')) setExpandedMenu('vendors');
    if (location.pathname.includes('/rings')) setExpandedMenu('rings');
    if (location.pathname.includes('/ceremony')) setExpandedMenu('ceremony');
  }, [location.pathname]);

  const baseUrl = `/app/w/${weddingId}`;

  // Helper to determine if a menu item should be shown/locked
  const getItem = (path: string, icon: any, label: string, featureKey?: string) => {
    const isLocked = featureKey ? !hasFeature(featureKey as any) : false;
    return { to: `${baseUrl}${path}`, icon, label, isLocked };
  };

  const vendorCategories = wedding?.vendor_types?.length ? wedding.vendor_types : ['Fotograf', 'Místo', 'Hudba'];
  
  // FINAL MENU ORDER: Consistent, Logical, Compact
  const navItems = [
    // 1. Dashboard
    getItem('/dashboard', LayoutDashboard, t('nav.dashboard')),
    // 2. Timeline
    getItem('/overview', Hourglass, t('nav.timeline')),
    // 3. Tasks
    getItem('/tasks', CheckSquare, t('nav.tasks')),
    // 4. Schedule
    getItem('/schedule', CalendarDays, t('nav.schedule')),
    // 5. Entertainment
    getItem('/entertainment', PartyPopper, t('nav.entertainment')),
    // 6. Svatební tým
    getItem('/wedding-team', UsersRound, t('nav.wedding_team')),
    
    { divider: true },
    
    // 7. Guests
    getItem('/guests', Users, t('nav.guests')),
    // 7. Seating
    getItem('/seating', Armchair, t('nav.seating'), 'seating'),
    // 8. Budget
    getItem('/budget', Wallet, t('nav.budget'), 'budget'),
    
    { divider: true },
    
    // 9. Vendors (Expandable)
    {
      ...getItem('/vendors', Store, t('nav.vendors')),
      id: 'vendors',
      children: vendorCategories.map(cat => ({
        label: cat,
        to: `${baseUrl}/vendors#${slugify(cat)}`
      }))
    },
    // 10. Rings (Expandable)
    {
      ...getItem('/rings', Gem, t('nav.rings')),
      id: 'rings',
      children: [
        { label: t('rings.bride'), to: `${baseUrl}/rings#nevesta` },
        { label: t('rings.groom'), to: `${baseUrl}/rings#zenich` }
      ]
    },
    // 11. Ceremony (Expandable)
    {
      ...getItem('/ceremony', ScrollText, t('nav.ceremony')),
      id: 'ceremony',
      children: [
         { label: t('ceremony.sections.info'), to: `${baseUrl}/ceremony#zaklad` },
         { label: t('ceremony.sections.contacts'), to: `${baseUrl}/ceremony#kontakty` },
         { label: t('ceremony.sections.steps'), to: `${baseUrl}/ceremony#prubeh` },
         { label: t('ceremony.sections.checklist'), to: `${baseUrl}/ceremony#checklist` },
      ]
    },
    
    { divider: true },
    
    // 12. Notes
    getItem('/notes', FileText, t('nav.notes')),
    // 13. Templates
    getItem('/templates', Mail, t('nav.templates'), 'templates'),
    
    { divider: true },
    
    // 14. Settings
    getItem('/settings', Settings, t('nav.settings')),
  ];

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-900 transition-colors duration-300 font-sans">
      {/* Sidebar - Desktop persistent / Mobile drawer */}
      <aside
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 w-64 z-50 flex flex-col
          bg-white dark:bg-slate-900
          lg:m-4 lg:rounded-2xl lg:shadow-sm
          border-r lg:border border-slate-200/80 dark:border-slate-800
          transition-transform duration-300 ease-out
          lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        role={!isLg && isSidebarOpen ? 'dialog' : undefined}
        aria-modal={!isLg && isSidebarOpen ? true : undefined}
        aria-label={!isLg && isSidebarOpen ? 'Hlavní navigace' : undefined}
      >
        <div className="h-16 lg:h-20 px-5 flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <Link to="/app" className="w-9 h-9 rounded-xl flex items-center justify-center bg-brand-600 dark:bg-brand-600 shrink-0" aria-label="MarryPlan – úvod">
            <Heart size={18} className="text-white" fill="white" />
          </Link>
          <Link to="/app" className="text-base font-semibold text-slate-900 dark:text-white truncate">MarryPlan</Link>
        </div>

        <nav className="px-3 py-5 space-y-1 flex-1 min-h-0 overflow-y-auto custom-scrollbar" aria-label="Navigace sekcí">
          {navItems.map((item: any, i) => (
            item.divider ? (
              <div key={`d-${i}`} className="h-px bg-slate-100 dark:bg-slate-800/50 my-4 mx-3" />
            ) : (
              <div key={item.to} className="relative group/item">
                 <SidebarItem
                  to={item.to}
                  icon={item.icon}
                  label={item.label}
                  active={location.pathname === item.to || (item.children && item.children.some((c: any) => location.pathname + (location.hash || '') === c.to))}
                  isLocked={item.isLocked}
                  children={item.children}
                  isExpanded={expandedMenu === item.id}
                  onToggleExpand={() => setExpandedMenu(prev => prev === item.id ? null : item.id)}
                  currentPath={location.pathname}
                  currentHash={location.hash || ''}
                  onClick={() => {
                    if (!item.isLocked) {
                      setIsSidebarOpen(false);
                      if (item.children) setExpandedMenu(item.id);
                    }
                  }}
                />
                {item.isLocked && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-md font-medium pointer-events-none">
                    Pro
                  </span>
                )}
              </div>
            )
          ))}
          <div className="h-10"></div>
        </nav>
      </aside>

      {/* Main Content - light gray background workspace */}
      <div className="flex-1 lg:ml-72 flex flex-col min-w-0">
        <header className="h-14 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 sticky top-0 z-40 px-4 sm:px-6 flex items-center justify-between lg:mx-4 lg:mt-4 lg:mb-2 lg:rounded-2xl lg:border">
          <div className="flex items-center gap-3">
            <button
              ref={menuButtonRef}
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              aria-label="Otevřít menu"
              aria-expanded={isSidebarOpen}
            >
              <Menu size={18} />
            </button>
            {wedding && (
              <div className="hidden sm:block">
                <h2 className="font-semibold text-slate-900 dark:text-white text-sm">{wedding.title}</h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">{wedding.location}</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <AiTriggerButton />
            
            {wedding?.plan === 'FREE' && (
              <Link to={`${baseUrl}/settings`} className="hidden sm:flex bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] px-3 py-1.5 rounded-lg font-bold uppercase tracking-wide items-center gap-1 hover:opacity-90 transition-opacity">
                 UPGRADE
              </Link>
            )}
            
            <button 
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="p-2 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:px-8 max-w-6xl mx-auto w-full">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Wrapper that shows AI components only when authenticated
const AuthenticatedAiComponents = () => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return null;
  }
  
  return (
    <>
      <GlobalAiFab />
      <AiDrawer />
    </>
  );
};

/** Staré odkazy /#/public/:slug → /pozvanka/:slug */
function InvitationLegacyRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`${INVITATION_PUBLIC_PATH}/${slug}`} replace />;
}

/** Záložní přesměrování pro záložky s HashRouterem (#/public/...) */
function LegacyHashPublicRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const hash = window.location.hash;
    const m = hash.match(/^#\/public\/([^/?#]+)/);
    if (m?.[1]) {
      navigate(`${INVITATION_PUBLIC_PATH}/${decodeURIComponent(m[1])}`, { replace: true });
    }
  }, [navigate]);
  return null;
}

// Main App component
const App = () => {
  return (
    <AuthProvider>
      <WeddingProvider>
        <AiContextProvider>
          <BrowserRouter>
            <LegacyHashPublicRedirect />
            {/* AI Components - only show when authenticated */}
            <AuthenticatedAiComponents />
            
            <Routes>
              {/* Veřejná pozvánka — zapamatovatelná URL bez hash */}
              <Route path={`${INVITATION_PUBLIC_PATH}/:slug`} element={<PublicPage />} />
              <Route path="/public/:slug" element={<InvitationLegacyRedirect />} />
              
              {/* Login Page */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected Routes */}
              <Route path="/app" element={<ProtectedRoute><HomeLayout /></ProtectedRoute>}>
                <Route index element={<WeddingsPage />} />
              </Route>

              {/* Wedding Detail Views */}
              <Route path="/app/w/:weddingId" element={<ProtectedRoute><WeddingLayout /></ProtectedRoute>}>
                <Route path="overview" element={<Timeline />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="guests" element={<Guests />} />
                <Route path="tasks" element={<Tasks />} />
                <Route path="schedule" element={<Schedule />} />
                <Route path="entertainment" element={<Entertainment />} />
                <Route path="wedding-team" element={<WeddingTeam />} />
                <Route path="budget" element={<Budget />} />
                <Route path="vendors" element={<Vendors />} />
                <Route path="rings" element={<RingsPage />} />
                <Route path="ceremony" element={<CeremonyPage />} />
                <Route path="seating" element={<SeatingPage />} />
                <Route path="notes" element={<Notes />} />
                <Route path="templates" element={<Templates />} />
                <Route path="settings" element={<SettingsPage />} />
                {/* Default redirect within wedding context */}
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
              
              {/* Global Redirects */}
              <Route path="/" element={<Navigate to="/app" replace />} />
              <Route path="*" element={<Navigate to="/app" replace />} />
            </Routes>
          </BrowserRouter>
        </AiContextProvider>
      </WeddingProvider>
    </AuthProvider>
  );
};

export default App;
